import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { SignOffMode } from "../src/sign-off-mode.ts";
import { type EditorLinks, linkScheme, normalizeLinkHref } from "./editor-links.tsx";

/*
 * Hints on a rep's own edits (`docs/2026-10-04-rep-edit-hints.md`). A rep's edit is saved through
 * HTML cleaning and nothing else, so this is the only thing that tells them a link has no https://,
 * a link has no text, the reply is empty or the ending is missing. In the reply box it also reads
 * how the reply closes (docs/2026-11-03-reply-box-mailbox-signature.md): no signature, two
 * closings, a name other than the sending mailbox's, Markdown marks in Plain text.
 *
 * Everything here is decided from the editor's own document, in the page, by fixed rules. It asks
 * no model, reads no approved list and sends nothing anywhere. A hint is advice: nothing in this
 * file is an input to what disables Approve (`src/approve-gate.ts`).
 */

/** One link in the draft: its address, its text, and where it sits in the editor's document. */
export type DraftLink = {
  readonly href: string;
  readonly text: string;
  readonly from: number;
  readonly to: number;
};
/** A run of text in the draft and where it starts. */
export type DraftText = { readonly text: string; readonly from: number };
/** How this draft is set to end, as the revision stores it; null when the ending is in the body. */
export type DraftHintSignOff = { readonly mode: SignOffMode; readonly plainText: string } | null;

export type DraftHintKind =
  | "link_no_scheme"
  | "link_http"
  | "link_scheme"
  | "link_malformed"
  | "mailto_invalid"
  | "link_empty_text"
  | "tag_as_text"
  | "empty_body"
  | "missing_ending"
  | "no_signature"
  | "two_closings"
  | "other_name"
  | "markdown_text";

export type DraftHintFix =
  | { readonly kind: "set_href"; readonly href: string; readonly label: string }
  | { readonly kind: "remove_link"; readonly label: string }
  | { readonly kind: "edit_link"; readonly label: string }
  | { readonly kind: "select"; readonly label: string }
  | { readonly kind: "add_signature"; readonly label: string };

export type DraftHint = {
  readonly id: string;
  readonly kind: DraftHintKind;
  /** One sentence naming the spot and what is off about it. */
  readonly message: string;
  /** Where in the editor's document, or null for a hint about the draft as a whole. */
  readonly at: { readonly from: number; readonly to: number } | null;
  /** The one click that fixes it or takes the rep to it; null when there is none. */
  readonly fix: DraftHintFix | null;
};

/** Markup typed or pasted as characters: `<b>`, `</p>`, `<br/>`, `<a href="…">`. */
const TAG_AS_TEXT = /<\/?[a-z][a-z0-9]*(?:\s[^<>]*)?\/?>/i;

const quoted = (text: string): string => {
  const clean = text.replace(/\s+/g, " ").trim();
  return `“${clean.length > 40 ? `${clean.slice(0, 39)}…` : clean}”`;
};
const shown = (href: string): string => (href.length > 60 ? `${href.slice(0, 59)}…` : href);

const ENDING_LABEL: Record<Exclude<SignOffMode, "none">, string> = {
  first_name: "“Best,” and a first name",
  full_signature: "the full signature",
};

function hrefHint(link: DraftLink): Pick<DraftHint, "kind" | "message" | "fix"> | null {
  const { href } = link;
  const name = quoted(link.text);
  const edit: DraftHintFix = { kind: "edit_link", label: "Edit link" };
  const result = normalizeLinkHref(href);
  if (result.ok) {
    if (result.href === href) return null;
    const email = result.href.startsWith("mailto:");
    return {
      kind: "link_no_scheme",
      message: email
        ? `The link ${name} points to the email address ${shown(href)} without mailto: in front.`
        : `The link ${name} points to ${shown(href)}, which has no https:// in front.`,
      fix: { kind: "set_href", href: result.href, label: email ? "Add mailto:" : "Add https://" },
    };
  }
  const scheme = linkScheme(href);
  if (scheme === "http") {
    const secure = normalizeLinkHref(href.trim().replace(/^http:/i, "https:"));
    if (secure.ok)
      return {
        kind: "link_http",
        message: `The link ${name} uses http://, not https://.`,
        fix: { kind: "set_href", href: secure.href, label: "Use https://" },
      };
  }
  if (scheme === "mailto")
    return {
      kind: "mailto_invalid",
      message: `The link ${name} is a mailto: link without an email address.`,
      fix: edit,
    };
  if (scheme !== null && scheme !== "http" && scheme !== "https")
    return {
      kind: "link_scheme",
      message: `The link ${name} uses ${scheme}:. Only https:// and mailto: links are kept when a draft is saved.`,
      fix: edit,
    };
  return {
    kind: "link_malformed",
    message:
      href.trim() === ""
        ? `The link ${name} has no address.`
        : `The link ${name} points to ${shown(href)}, which is not a web address.`,
    fix: edit,
  };
}

/** Who the reply goes from, for reading how it closes: the mailbox's signature and sender's name. */
export type ClosingCheck = {
  readonly signature: string | null;
  readonly sender: string | null;
} | null;

/** Closing words, alone on their line or before a name: English, Polish and German mail. */
const CLOSING_WORDS =
  "best|best regards|kind regards|warm regards|regards|warmly|many thanks|thanks|thank you|cheers|all the best|sincerely|talk soon|speak soon|pozdrawiam|pozdrawiam serdecznie|serdecznie pozdrawiam|z pozdrowieniami|z wyrazami szacunku|dziękuję|dzięki|viele grüße|beste grüße|liebe grüße|mit freundlichen grüßen|grüße";
/** Longest first, so "Best regards" is one closing and not "Best" signed "regards". */
const CLOSING = new RegExp(
  `^(?:${CLOSING_WORDS.split("|")
    .sort((a, b) => b.length - a.length)
    .join("|")})[,!.]?(?:\\s+(.+))?$`,
  "iu",
);
/** A name as a closing signs it: one or two capitalised words. "Thanks for the reply" is no closing. */
const NAME = /^\p{Lu}[\p{L}'’.-]*(?:\s+\p{Lu}[\p{L}'’.-]*)?[.!]?$/u;
const MARKDOWN =
  /\*\*[^*\n]+\*\*|__[^_\n]+__|^#{1,6}\s\S.*$|\[[^\]\n]+\]\((?:https?:|mailto:)[^)\s]+\)/mu;
/** How far up from the end a closing is looked for; above it, "Thanks!" opens a reply. */
const TAIL = 8;

const flat = (text: string): string => text.replace(/\s+/gu, " ").trim().toLowerCase();

type Closing = { readonly line: string; readonly name: string | null };

/** Who the reply goes from, read once: the sender's words and the signature's lines, flattened. */
type From = {
  readonly sender: readonly string[];
  readonly senderWords: ReadonlySet<string>;
  readonly signatureLines: readonly string[];
};

function readFrom(check: NonNullable<ClosingCheck>): From {
  const sender = check.sender === null ? [] : flat(check.sender).split(" ");
  const signatureLines = (check.signature ?? "")
    .split("\n")
    .map(flat)
    .filter((line) => line !== "");
  return { sender, senderWords: new Set(sender), signatureLines };
}

/** The closing lines near the end, each with the name it signs, on its own line or after it. */
function readClosings(tail: readonly string[]): Closing[] {
  const closings: Closing[] = [];
  tail.forEach((line, index) => {
    const match = CLOSING.exec(line);
    if (!match) return;
    const inline = match[1]?.trim();
    if (inline !== undefined && !NAME.test(inline)) return;
    const next = tail[index + 1];
    closings.push({ line, name: inline ?? (next !== undefined && NAME.test(next) ? next : null) });
  });
  return closings;
}

/** A closing signed by someone who is neither the sender nor a line of the signature. */
function strangerOf(closings: readonly Closing[], from: From): string | null {
  if (from.senderWords.size === 0) return null;
  const stranger = closings.find(
    ({ name }) =>
      name !== null &&
      !from.signatureLines.includes(flat(name)) &&
      !flat(name)
        .split(" ")
        .some((word) => from.senderWords.has(word)),
  );
  return stranger?.name ?? null;
}

/** Signed: a closing with a name, or a line that is the sender's name or the signature's first. */
function isSigned(tail: readonly string[], closings: readonly Closing[], from: From): boolean {
  if (closings.some((closing) => closing.name !== null)) return true;
  const name = from.sender.join(" ");
  const first = from.sender[0] ?? "";
  return tail.some((line) => {
    const words = flat(line);
    return words === name || words === first || words === from.signatureLines[0];
  });
}

const whole = (id: DraftHintKind, message: string, fix: DraftHintFix | null = null): DraftHint => ({
  id,
  kind: id,
  message,
  at: null,
  fix,
});

function unsignedHint(check: NonNullable<ClosingCheck>): DraftHint | null {
  if (check.signature !== null)
    return whole("no_signature", "The reply ends without the mailbox's signature.", {
      kind: "add_signature",
      label: "Add signature",
    });
  if (check.sender !== null)
    return whole("no_signature", `The reply is not signed. It goes from ${check.sender}.`);
  return null;
}

/**
 * How the reply closes, read from its whole text. Each finding is advice with no position: the box
 * still sends what it holds.
 */
function closingHints(text: string, check: NonNullable<ClosingCheck>, plain: boolean): DraftHint[] {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  if (lines.length === 0) return [];
  const tail = lines.slice(Math.max(1, lines.length - TAIL));
  const from = readFrom(check);
  const closings = readClosings(tail);
  const hints: DraftHint[] = [];
  if (closings.length > 1)
    hints.push(
      whole(
        "two_closings",
        `The reply closes twice: ${closings.map((closing) => quoted(closing.line)).join(" and ")}.`,
      ),
    );
  const stranger = strangerOf(closings, from);
  if (stranger !== null && check.sender !== null)
    hints.push(
      whole("other_name", `Signed ${quoted(stranger)}, but this reply goes from ${check.sender}.`),
    );
  const unsigned = isSigned(tail, closings, from) ? null : unsignedHint(check);
  if (unsigned) hints.push(unsigned);
  const marks = plain ? MARKDOWN.exec(text) : null;
  if (marks)
    hints.push(
      whole(
        "markdown_text",
        `${quoted(marks[0])} is Markdown. A plain-text email shows the marks as typed.`,
      ),
    );
  return hints;
}

/**
 * What to check in a draft, in the order it appears. Deterministic: the same document and ending
 * always give the same list.
 */
export function draftHints(input: {
  readonly links: readonly DraftLink[];
  readonly texts: readonly DraftText[];
  readonly signOff: DraftHintSignOff;
  /** The reply box's: how its whole text closes, and whether it is Plain text. */
  readonly closing?: {
    readonly text: string;
    readonly plain: boolean;
    readonly check: ClosingCheck;
  };
}): DraftHint[] {
  const hints: DraftHint[] = [];
  for (const link of input.links) {
    const at = { from: link.from, to: link.to };
    if (link.text.trim() === "") {
      hints.push({
        id: `link_empty_text:${link.from}`,
        kind: "link_empty_text",
        message: `A link to ${shown(link.href.replace(/^(https?:\/\/|mailto:)/i, ""))} has no text, so there is nothing to click.`,
        at,
        fix: { kind: "remove_link", label: "Remove link" },
      });
      continue;
    }
    const found = hrefHint(link);
    if (found) hints.push({ id: `${found.kind}:${link.from}`, at, ...found });
  }
  for (const run of input.texts) {
    const match = TAG_AS_TEXT.exec(run.text);
    if (!match) continue;
    const from = run.from + match.index;
    hints.push({
      id: `tag_as_text:${from}`,
      kind: "tag_as_text",
      message: `${quoted(match[0])} is typed as text, so it is sent as those characters and not as formatting.`,
      at: { from, to: from + match[0].length },
      fix: { kind: "select", label: "Show in draft" },
    });
  }
  hints.sort((a, b) => (a.at?.from ?? 0) - (b.at?.from ?? 0));
  if (input.texts.every((run) => run.text.trim() === ""))
    hints.push({
      id: "empty_body",
      kind: "empty_body",
      message: "The reply has no text.",
      at: null,
      fix: null,
    });
  const { signOff } = input;
  if (signOff !== null && signOff.mode !== "none" && signOff.plainText.trim() === "")
    hints.push({
      id: "missing_ending",
      kind: "missing_ending",
      message: `The ending is empty. This draft is set to end with ${ENDING_LABEL[signOff.mode]}, and there was nothing to add.`,
      at: null,
      fix: null,
    });
  const { closing } = input;
  if (closing?.check && closing.text.trim() !== "")
    hints.push(...closingHints(closing.text, closing.check, closing.plain));
  return hints;
}

/** The links and text of the editor's document, with their positions. */
export function readDraft(editor: Editor): {
  links: DraftLink[];
  texts: DraftText[];
} {
  const links: DraftLink[] = [];
  const texts: DraftText[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    const text = node.text ?? "";
    texts.push({ text, from: pos });
    const mark = node.marks.find((candidate) => candidate.type.name === "link");
    if (!mark) return;
    const href = String(mark.attrs["href"] ?? "");
    const to = pos + node.nodeSize;
    const last = links.at(-1);
    // One link whose text carries bold or italic is several text nodes; it is still one link.
    if (last && last.to === pos && last.href === href)
      links[links.length - 1] = { ...last, text: last.text + text, to };
    else links.push({ href, text, from: pos, to });
  });
  return { links, texts };
}

/** Carries out a hint's one click. The address field is the link controls' own. */
export function applyHintFix(editor: Editor, hint: DraftHint, links: EditorLinks): void {
  const { fix, at } = hint;
  if (!fix || !at) return;
  switch (fix.kind) {
    case "set_href":
      editor.chain().setTextSelection(at).setLink({ href: fix.href }).setTextSelection(at.to).run();
      return;
    case "remove_link":
      editor.chain().setTextSelection(at).unsetLink().setTextSelection(at.to).run();
      return;
    case "edit_link":
      // No focus on the editor here: the field takes it, and a late editor focus would close the field.
      editor.chain().setTextSelection(at).run();
      links.open();
      return;
    case "select":
      editor.chain().focus().setTextSelection(at).scrollIntoView().run();
      return;
    case "add_signature":
      return;
  }
}

export type DraftHints = {
  readonly editor: Editor | null;
  readonly hints: readonly DraftHint[];
  /** Check again now, without the pause: after a fix, so the fixed hint goes at once. */
  readonly recheck: () => void;
};

/** How long after the last keystroke the draft is checked. */
export const HINT_DELAY_MS = 400;

/**
 * The hints for one editor: checked when it mounts, again a pause after each edit, and at once when
 * the draft's ending changes. Checking is a walk over the document, never a request.
 */
export function useDraftHints(
  editor: Editor | null,
  signOff: DraftHintSignOff,
  options: { readonly delayMs?: number; readonly closing?: ClosingCheck } = {},
): DraftHints {
  const delayMs = options.delayMs ?? HINT_DELAY_MS;
  const [hints, setHints] = useState<readonly DraftHint[]>([]);
  const mode = signOff?.mode ?? null;
  const ending = signOff?.plainText ?? null;
  const signature = options.closing?.signature ?? null;
  const sender = options.closing?.sender ?? null;
  const reads = options.closing !== undefined && options.closing !== null;
  const recheck = useCallback((): void => {
    if (!editor || editor.isDestroyed) return;
    const next = draftHints({
      ...readDraft(editor),
      signOff: mode === null || ending === null ? null : { mode, plainText: ending },
      ...(reads
        ? {
            closing: {
              text: editor.getText({ blockSeparator: "\n\n" }),
              plain: false,
              check: { signature, sender },
            },
          }
        : {}),
    });
    // The same list keeps its identity, so nothing below re-renders or re-announces on a keystroke.
    setHints((current) =>
      current.length === next.length &&
      current.every(
        (hint, index) => hint.id === next[index]?.id && hint.message === next[index]?.message,
      )
        ? current
        : next,
    );
  }, [editor, mode, ending, reads, signature, sender]);
  useEffect(() => {
    if (!editor) return;
    recheck();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onUpdate = (): void => {
      if (timer !== null) clearTimeout(timer);
      if (delayMs <= 0) {
        recheck();
        return;
      }
      timer = setTimeout(recheck, delayMs);
    };
    editor.on("update", onUpdate);
    return () => {
      editor.off("update", onUpdate);
      if (timer !== null) clearTimeout(timer);
    };
  }, [editor, recheck, delayMs]);
  return { editor, hints, recheck };
}

const countLabel = (count: number): string =>
  count === 1 ? "1 thing to check" : `${count} things to check`;
/** More than this many are folded behind one button, so the list never outgrows the draft. */
const VISIBLE = 5;

/**
 * The hints, drawn as the last row of the editor's own frame. `id` names the title, so Approve can
 * point at it with `aria-describedby`. The count is announced in a region of its own, and only when
 * it changes.
 */
export function DraftHintsPanel({
  hints: state,
  links,
  id,
  frozen = false,
  verb = "approve",
  onAddSignature,
}: {
  readonly hints: DraftHints;
  readonly links: EditorLinks;
  readonly id: string;
  /** While the editor is frozen (a save in flight), a fix would be lost: the buttons wait. */
  readonly frozen?: boolean;
  /** What the hints never stop: the agent's Approve, or the reply box's Send. */
  readonly verb?: "approve" | "send";
  /** The reply box's "Add signature", for the hint that the reply has none. */
  readonly onAddSignature?: () => void;
}) {
  const { hints, editor } = state;
  const [all, setAll] = useState(false);
  const list = useRef<HTMLUListElement | null>(null);
  // Set by a fix that changed the draft: the row it sat in is gone, and the focus must not be lost.
  const refocus = useRef(false);
  const had = useRef(false);
  const count = hints.length;
  useEffect(() => {
    if (count > 0) had.current = true;
    if (!refocus.current) return;
    refocus.current = false;
    const next = list.current?.querySelector<HTMLButtonElement>("button");
    if (next) next.focus();
    else editor?.commands.focus();
  });
  const announce =
    count > 0
      ? `${countLabel(count)} in this ${verb === "send" ? "reply" : "draft"}. You can still ${verb}.`
      : had.current
        ? `Nothing left to check in this ${verb === "send" ? "reply" : "draft"}.`
        : "";
  const visible = all ? hints : hints.slice(0, VISIBLE);
  const hidden = count - visible.length;
  return (
    <>
      <span className="lcr-visually-hidden" role="status">
        {announce}
      </span>
      {count > 0 && (
        <section className="lcr-hints" aria-labelledby={id}>
          <p className="lcr-hints-head">
            <span className="lcr-hints-title" id={id}>
              {countLabel(count)}
            </span>{" "}
            <span className="lcr-hints-free">You can still {verb}.</span>
          </p>
          <ul ref={list}>
            {visible.map((hint) => (
              <li key={hint.id}>
                <span className="lcr-hint-text">{hint.message}</span>
                {hint.fix && (hint.fix.kind === "add_signature" ? onAddSignature : editor) && (
                  <button
                    type="button"
                    className="lcr-draft-source-toggle"
                    disabled={frozen}
                    onClick={() => {
                      if (frozen) return;
                      if (hint.fix?.kind === "add_signature") {
                        onAddSignature?.();
                        state.recheck();
                        return;
                      }
                      if (!editor) return;
                      const changes =
                        hint.fix?.kind === "set_href" || hint.fix?.kind === "remove_link";
                      applyHintFix(editor, hint, links);
                      if (!changes) return;
                      refocus.current = true;
                      state.recheck();
                    }}
                  >
                    {hint.fix.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {hidden > 0 && (
            <button type="button" className="lcr-draft-source-toggle" onClick={() => setAll(true)}>
              Show {hidden} more
            </button>
          )}
        </section>
      )}
    </>
  );
}
